import { CommonModule } from '@angular/common';
import { Component, TemplateRef } from '@angular/core';
import {
  FormlyFieldProps as CoreFormlyFieldProps,
  FieldWrapper,
  FormlyFieldConfig,
} from '@ngx-formly/core';

import { IconFieldModule } from 'primeng/iconfield';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { Tooltip } from 'primeng/tooltip';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';

/** Il tooltip di un'addon è largo quanto serve ma mai più dello schermo: resta leggibile anche da telefono. */
const ADDON_TOOLTIP_CLASS = 'max-w-[min(18rem,calc(100vw_-_4rem))]';

export interface FormlyFieldProps extends CoreFormlyFieldProps {
  hideRequiredMarker?: boolean;
  hideLabel?: boolean;
  addonLeft?: any;
  addonRight?: any;
  addonRightDetached?: boolean;
  detachedTemplate?: TemplateRef<unknown>;
  labelKey?: string;
  warning?: string;
  icon?: string;
  iconPosition?: 'left' | 'right';
  inline?: boolean;
  /**
   * Keeps the (unlabeled-field) error slot always in the layout, so its own
   * line-height reserves the space whether or not a message is currently
   * showing — avoids the field's own height (and anything laid out relative
   * to it) shifting when validation state toggles. Opt-in; every other
   * consumer keeps today's conditional rendering.
   */
  reserveErrorSpace?: boolean;
  /**
   * Shows the validation message under the field (next to the description)
   * even when the field has a label; by default it sits next to the label.
   */
  errorBelow?: boolean;
}

@Component({
  selector: 'formly-wrapper-primeng-form-field',
  standalone: true,
  imports: [
    CommonModule,
    InputGroup,
    InputGroupAddon,
    ...FORM_IMPORTS,
    IconFieldModule,
    InputIconModule,
    Tooltip,
  ],
  template: `
    <div
      class="p-field {{
        props['inline'] ? 'flex flex-wrap gap-2 items-center' : ''
      }}"
    >
      @if (props.label && props.hideLabel !== true) {
        <div class="flex items-baseline flex-wrap">
          <label
            class="ml-(--p-inputtext-padding-x)"
            [for]="id"
            [innerHTML]="props.label"
          >
          </label>

          @if (showError && !props.errorBelow) {
            <small
              class="text-(--p-form-field-invalid-placeholder-color) italic ml-(--p-inputtext-padding-x)"
            >
              <formly-validation-message
                class="ui-message-text"
                [field]="field"
              ></formly-validation-message>
            </small>
          }
        </div>
      }
      @if (props.addonLeft || props.addonRight) {
        <p-inputGroup
          [ngClass]="{
            'w-full': true,
            'ng-invalid p-invalid': showError || !formControl.valid,
            'addon-prefix': hasVisibleAddon('addonLeft'),
            'addon-suffix': hasVisibleAddon('addonRight'),
          }"
        >
          @for (addon of getAddons('addonLeft'); track $index) {
            @if (isAddonVisible(addon)) {
              <p-inputGroupAddon>
                <span
                  [ngStyle]="{
                    cursor: addon.onClick ? 'pointer' : 'inherit',
                  }"
                  [pTooltip]="getAddonTooltip(addon) ?? ''"
                  tooltipPosition="right"
                  tooltipEvent="both"
                  [tooltipStyleClass]="addonTooltipClass"
                  [attr.role]="getAddonTooltip(addon) ? 'img' : null"
                  [attr.tabindex]="getAddonTooltip(addon) ? 0 : null"
                  [attr.aria-label]="getAddonTooltip(addon)"
                  (click)="onAddonClick(addon, $event)"
                >
                  @if (getAddonIcon(addon)) {
                    <span class="{{ getAddonIcon(addon) }}"></span>
                  }
                  @if (getAddonText(addon)) {
                    <span [innerHTML]="getAddonText(addon)"></span>
                  }
                </span>
              </p-inputGroupAddon>
            }
          }

          @if (props.icon) {
            <p-iconfield class="field-slot" [class.field-invalid]="showError">
              @if (props.iconPosition != 'right') {
                <p-inputicon class="{{ props.icon }}" />
              }
              <ng-container #fieldComponent></ng-container>
              @if (props.iconPosition == 'right') {
                <p-inputicon class="{{ props.icon }}" />
              }
            </p-iconfield>
          } @else {
            <div class="field-slot" [class.field-invalid]="showError">
              <ng-container #fieldComponent></ng-container>
            </div>
          }

          @for (addon of getAddons('addonRight'); track $index) {
            @if (isAddonVisible(addon)) {
              <p-inputGroupAddon>
                <span
                  [ngStyle]="{
                    cursor: addon.onClick ? 'pointer' : 'inherit',
                  }"
                  [pTooltip]="getAddonTooltip(addon) ?? ''"
                  tooltipPosition="left"
                  tooltipEvent="both"
                  [tooltipStyleClass]="addonTooltipClass"
                  [attr.role]="getAddonTooltip(addon) ? 'img' : null"
                  [attr.tabindex]="getAddonTooltip(addon) ? 0 : null"
                  [attr.aria-label]="getAddonTooltip(addon)"
                  (click)="onAddonClick(addon, $event)"
                >
                  @if (getAddonIcon(addon)) {
                    <span class="{{ getAddonIcon(addon) }}"></span>
                  }
                  @if (getAddonText(addon)) {
                    <span [innerHTML]="getAddonText(addon)"></span>
                  }
                </span>
              </p-inputGroupAddon>
            }
          }
        </p-inputGroup>
      } @else {
        <div [class.field-invalid]="showError">
          <ng-container #fieldComponent></ng-container>
        </div>
      }

      @if (props.detachedTemplate) {
        <ng-container
          [ngTemplateOutlet]="$any(props.detachedTemplate)"
        ></ng-container>
      }

      <div class="flex flex-col gap-1">
        @if (props.description) {
          <small
            class="italic ml-(--p-inputtext-padding-x)"
            [innerHTML]="props.description"
          ></small>
        }

        @if (
          (showError || props.reserveErrorSpace) &&
          (props.errorBelow || !(props.label && props.hideLabel !== true))
        ) {
          <small
            class="text-(--p-form-field-invalid-placeholder-color) italic ml-(--p-inputtext-padding-x)"
          >
            <formly-validation-message
              class="ui-message-text"
              [field]="field"
            ></formly-validation-message>
          </small>
        }

        @if (props.warning) {
          <small
            class="italic text-(--p-message-warn-color) ml-(--p-inputtext-padding-x)"
            [innerHTML]="props.warning"
          ></small>
        }
      </div>
    </div>
  `,
  styles: `
    :host ::ng-deep .p-inputgroup {
      width: 100%;
    }

    :host ::ng-deep .p-inputgroup > .field-slot,
    :host ::ng-deep .p-inputgroup > p-iconfield.field-slot {
      flex: 1 1 auto;
      width: 1%;
      min-width: 0;
    }

    :host ::ng-deep .addon-suffix .field-slot .p-inputtext,
    :host ::ng-deep .addon-suffix .field-slot .p-inputnumber-input,
    :host ::ng-deep .addon-suffix .field-slot .p-select,
    :host ::ng-deep .addon-suffix .field-slot .p-multiselect,
    :host ::ng-deep .addon-suffix .field-slot textarea.p-textarea,
    :host ::ng-deep .addon-suffix .field-slot .p-password-input,
    :host ::ng-deep .addon-suffix .field-slot .p-password {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }

    :host ::ng-deep .addon-prefix .field-slot .p-inputtext,
    :host ::ng-deep .addon-prefix .field-slot .p-inputnumber-input,
    :host ::ng-deep .addon-prefix .field-slot .p-select,
    :host ::ng-deep .addon-prefix .field-slot .p-multiselect,
    :host ::ng-deep .addon-prefix .field-slot textarea.p-textarea,
    :host ::ng-deep .addon-prefix .field-slot .p-password-input,
    :host ::ng-deep .addon-prefix .field-slot .p-password {
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }

    :host ::ng-deep .field-invalid .p-inputtext,
    :host ::ng-deep .field-invalid .p-inputnumber-input,
    :host ::ng-deep .field-invalid .p-select,
    :host ::ng-deep .field-invalid .p-multiselect,
    :host ::ng-deep .field-invalid textarea.p-textarea,
    :host ::ng-deep .field-invalid .p-password-input,
    :host ::ng-deep .field-invalid .p-password {
      border-color: var(--p-form-field-invalid-border-color) !important;
    }
  `,
})
export class FormlyWrapperFormField extends FieldWrapper<
  FormlyFieldConfig<FormlyFieldProps>
> {
  readonly addonTooltipClass = ADDON_TOOLTIP_CLASS;

  hasVisibleAddon(side: 'addonLeft' | 'addonRight'): boolean {
    return this.getAddons(side).some((addon) => this.isAddonVisible(addon));
  }

  getAddons(side: 'addonLeft' | 'addonRight'): any[] {
    const addon = this.props[side];
    if (!addon) return [];
    return Array.isArray(addon) ? addon : [addon];
  }

  isAddonVisible(addon: any): boolean {
    if (!addon) return false;
    if (typeof addon.show === 'function') {
      return !!addon.show(this.props, this, this.field);
    }
    if (typeof addon.show === 'boolean') {
      return addon.show;
    }
    return true;
  }

  getAddonText(addon: any): string | null {
    if (!addon) return null;
    const raw =
      typeof addon.text === 'function'
        ? addon.text(this.props, this, this.field)
        : addon.text;
    return raw == null ? null : String(raw);
  }

  getAddonIcon(addon: any): string | null {
    if (!addon) return null;
    const raw =
      typeof addon.icon === 'function'
        ? addon.icon(this.props, this, this.field)
        : addon.icon;
    return raw == null ? null : String(raw);
  }

  /** Testo del tooltip PrimeNG che l'addon mostra al passaggio del mouse (o al tocco / focus); `null` se non ne ha. */
  getAddonTooltip(addon: any): string | null {
    if (!addon) return null;
    const raw =
      typeof addon.tooltip === 'function'
        ? addon.tooltip(this.props, this, this.field)
        : addon.tooltip;
    return raw == null || raw === '' ? null : String(raw);
  }

  private isAddonDisabled(addon: any): boolean {
    if (!addon) return false;
    if (typeof addon.disabled === 'function') {
      return !!addon.disabled(this.props, this, this.field);
    }
    return !!addon.disabled;
  }

  onAddonClick(addon: any, $event: any) {
    if (this.isAddonDisabled(addon)) return;
    if (addon.onClick) {
      addon.onClick(this.props, this, $event, this.field);
    }
  }
}
